import React, {Fragment, StrictMode, useState} from "react";
import {CalendarCheckIcon, PaperPlaneIcon, RatingStars} from "@App/Components/common/Icons";
import {Hr} from "@App/Components/Hr";
import {Calculation, Garage, Service} from "@App/types";
import {Transition} from "@headlessui/react";
import {useDates} from "@App/hooks/useDates";
import {GarageListItemPrice} from "@App/Components/GarageListItemPrice";
import {useAssetUrl} from "@App/hooks/useAssetUrl";
import {useConfig} from "@App/hooks/useConfig";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {State} from "@App/state";
import {BookingType} from "@App/Components/BookingType";
import {
    GarageHasAvailableDays,
    GarageHasBillingGroups,
    GarageHasPhone,
    GarageIsBookable
} from "@App/utils/conditionals/garages";
import {GarageIsBookableOnArval, GarageIsNotBookableAndHasPhone} from "@App/utils/conditionals/combinations";
import {OrganizationIsArval} from "@App/utils/conditionals/organization";
import {GarageListItemBadge} from "@App/Components/Partials/GarageListItemBadge";
import {GarageListItemPremiumServices} from "@App/Components/Partials/GarageListItemPremiumServices";
import {ServiceHasCustomImagePlaceholder, ServiceShouldDisplayReviews} from "@App/utils/conditionals/services";

type GarageListItemProps = {
    garage: Garage
    showAsNonBookable: boolean
    prefix?: string
    showCheckpoint?: boolean
    fixedPrice?: boolean
    onClick: (garage: Garage) => void
    nearest?: boolean
    earliestAvailable?: boolean
    bestReviewed?: boolean
    authorized?: boolean
    cheapest?: boolean
    highlighted?: boolean
    service?: Service
    calculation?: Calculation
};

export const GarageListItem = React.memo<GarageListItemProps>((props: GarageListItemProps) => {

    const {t} = useTranslation() // i18next
    const {formatShortDate} = useDates()
    const {url} = useAssetUrl()
    const {displayPrices, displayPremiumServices, organization} = useConfig()
    const place = useSelector((state: State) => state.orders.place)
    const service = useSelector((state: State) => state.orders.service)

    const [showPhoneNumber, setShowPhoneNumber] = useState(false)

    const onClick = (garage: Garage) => {
        (GarageIsBookable(garage) && !props.showAsNonBookable)
        && props.onClick
        && props.onClick(garage)
    }

    const togglePhoneNumber = () => {
        setShowPhoneNumber(true)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'reveal phone number',
            'garage': props.garage,
            'service': props.service,
            'location': place,
            'organization': organization.id
        });
        if (typeof plausible !== 'undefined') {
            plausible('reveal garage phone number', {
                props: {
                    organization: organization.id,
                    service: props.service.id,
                    garage: props.garage.id,
                    garageName: props.garage.name
                }
            })
        }
    }

    return (
        <div className="w-full">
            <Transition
                appear={true}
                show={true}
                enter={"transition transform duration-300 ease-out"}
                enterFrom={"translate-x-4 opacity-0"}
                enterTo={"translate-x-0 opacity-100"}
                leave={"transition transform duration-300 ease-in"}
                leaveFrom={"opacity-100"}
                leaveTo={"opacity-0"}>
                {/* use StrictMode after Headless UI Transition to prevent exceptions from external components */}
                <StrictMode>
                    <div
                        className={`
                        garageItem min-h-[300px] relative rounded w-full lg:max-w-full bg-white
                        ${props.highlighted ? 'border border-primary' : ''}
                        ${(props.nearest || props.earliestAvailable || props.bestReviewed || props.authorized || props.cheapest) ? 'lg:pt-5 xl:pt-0' : ''}
                        shadow text-sm mb-3 pb-1 lg:pb-5 lg:px-5`}>
                        {(props.nearest || props.earliestAvailable || props.bestReviewed || props.authorized || props.cheapest) && (
                            <GarageListItemBadge nearest={props.nearest} earliestAvailable={props.earliestAvailable}
                                                 authorized={props.authorized} cheapest={props.cheapest}
                                                 bestReviewed={props.bestReviewed} garage={props.garage}/>
                        )}
                        <div className="relative flex flex-col first justify-center">
                            {props.showCheckpoint && props.garage.checkpoint && props.garage.checkpoint.logo && (
                                <img
                                    src={url + ("/storage/" + props.garage.checkpoint.logo)}
                                    className={"rounded-t lg:rounded w-full h-12 mt-12 md:mt-0"}
                                    alt=""
                                    // loading={"lazy"}
                                />
                            )}
                            {(!props.showCheckpoint || !props.garage.checkpoint || !props.garage.checkpoint.logo) && (
                                !!props.garage.cover_image
                                    ? <img src={url + "/storage/" + props.garage.cover_image}
                                           className={"rounded-t lg:rounded w-full h-auto"} alt=""
                                        // loading={"lazy"}
                                    />
                                    : <div className={'flex items-center justify-center text-center text-lg text-neutral-700 font-serif font-bold rounded-t lg:rounded bg-neutral-300 w-full h-full min-h-[250px] lg:min-h-min p-4'}>
                                        {service && ServiceHasCustomImagePlaceholder(service) ? (
                                            <span
                                                dangerouslySetInnerHTML={{__html: service.pivot.display_image_placeholder.replace('%ORGANIZATION%', organization.name)}}/>
                                        ) : (
                                            <>
                                                {organization.name}
                                                <br/>
                                                {t('garageList.serviceLocation')}
                                            </>
                                        )}
                                    </div>
                            )}
                        </div>
                        <div className="px-5 lg:px-0 mt-4 second lg:mb-4">
                            <div className={`font-serif font-bold text-lg mb-1 ${(GarageIsBookable(props.garage) && !props.showAsNonBookable) ? 'cursor-pointer' : ''}`} onClick={() => onClick(props.garage)}>
                                <span className={'mr-2'}>{props.prefix} {props.garage.name}</span>
                                {GarageHasBillingGroups(props.garage) && (
                                    <span className={'font-sm sm:font-base text-sm font-normal'}>
                                         ({props.garage.billingGroups.map((billingGroup, index) => (
                                        <span>{billingGroup.display_name}{index < props.garage.billingGroups.length - 1 ? ", " : ""}</span>
                                    ))})
                                    </span>
                                )}

                            </div>
                            <div className="flex justify-between lg:justify-start">
                            <div className={'flex flex-col md:flex-row gap-x-3'}>
                            <div className="flex items-center order-1">
                                        <svg xmlns="http://www.w3.org/2000/svg"
                                             width="12.326"
                                             height="23.654"
                                             viewBox="0 0 384 512">
                                            <path
                                                fill="var(--color-primary)"
                                                d="M272 192C272 236.2 236.2 272 192 272C147.8 272 112 236.2 112 192C112 147.8 147.8 112 192 112C236.2 112 272 147.8 272 192zM192 240C218.5 240 240 218.5 240 192C240 165.5 218.5 144 192 144C165.5 144 144 165.5 144 192C144 218.5 165.5 240 192 240zM384 192C384 279.4 267 435 215.7 499.2C203.4 514.5 180.6 514.5 168.3 499.2C116.1 435 0 279.4 0 192C0 85.96 85.96 0 192 0C298 0 384 85.96 384 192H384zM192 32C103.6 32 32 103.6 32 192C32 207.6 37.43 229 48.56 255.4C59.47 281.3 74.8 309.4 92.14 337.5C126.2 392.8 166.6 445.7 192 477.6C217.4 445.7 257.8 392.8 291.9 337.5C309.2 309.4 324.5 281.3 335.4 255.4C346.6 229 352 207.6 352 192C352 103.6 280.4 32 192 32z"/>
                                        </svg>
                                        <div className={'font-xs sm:font-base'}>
                                            {props.garage.nationwide_service && (
                                                <Fragment>
                                                    {t('garageList.mobileService')}
                                                </Fragment>
                                            )}
                                            {!props.garage.nationwide_service && (
                                                <span>&nbsp;{props.garage.city} -  {props.garage.postcode} - {props.garage.street}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center order-0">
                                        <svg xmlns="http://www.w3.org/2000/svg"
                                             width="12.326"
                                             height="23.654"
                                             viewBox="0 0 512 512">
                                            <path fill="var(--color-primary)"
                                                  d="M440 96C440 109.3 429.3 120 416 120C402.7 120 392 109.3 392 96C392 82.75 402.7 72 416 72C429.3 72 440 82.75 440 96zM416 256H336C309.5 256 288 277.5 288 304C288 330.5 309.5 352 336 352H432C476.2 352 512 387.8 512 432C512 476.2 476.2 512 432 512H139.6C147.3 503.2 156.5 492.3 165.1 480H432C458.5 480 480 458.5 480 432C480 405.5 458.5 384 432 384H336C291.8 384 255.1 348.2 255.1 304C255.1 259.8 291.8 224 336 224H387.1C358.8 190.3 320 136.7 320 96C320 42.98 362.1 0 416 0C469 0 512 42.98 512 96C512 140.3 466 199.9 437.7 232.4C425.1 246.9 416 256 416 256zM468.4 133.7C476.5 117.5 480 104.7 480 96C480 60.65 451.3 32 416 32C380.7 32 352 60.65 352 96C352 104.7 355.5 117.5 363.6 133.7C371.5 149.4 382.2 165.7 393.6 180.8C401.3 191.1 409.1 200.6 416 208.6C422.9 200.6 430.7 191.1 438.4 180.8C449.8 165.7 460.5 149.4 468.4 133.7zM71.1 352C71.1 338.7 82.74 328 95.1 328C109.3 328 119.1 338.7 119.1 352C119.1 365.3 109.3 376 95.1 376C82.74 376 71.1 365.3 71.1 352zM117.7 488.4C115.7 490.7 113.8 492.8 112.1 494.8L112 494.8C102.5 505.5 96 512 96 512C96 512 86.9 502.9 74.3 488.4C45.98 455.9 0 396.3 0 352C0 298.1 42.98 256 96 256C149 256 192 298.1 192 352C192 392.8 152.9 446.7 124.6 480.4C122.2 483.2 119.9 485.9 117.7 488.4L117.7 488.4zM148.4 389.7C156.5 373.5 159.1 360.7 159.1 352C159.1 316.7 131.3 288 95.1 288C60.65 288 31.1 316.7 31.1 352C31.1 360.7 35.53 373.5 43.62 389.7C51.46 405.4 62.25 421.7 73.6 436.8C81.34 447.1 89.11 456.6 95.1 464.6C102.9 456.6 110.7 447.1 118.4 436.8C129.7 421.7 140.5 405.4 148.4 389.7z"/>
                                        </svg>
                                        <div className={'font-xs sm:font-base'}>
                                            {!props.garage.nationwide_service && (
                                                <Fragment>
                                                    &nbsp;{(props.garage.distance / 1000).toFixed(2).replace('.', ',')} km {props.garage.travel_time && (<>| {props.garage.travel_time} Minuten</>)}
                                                </Fragment>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {GarageIsBookable(props.garage) && ServiceShouldDisplayReviews(props.service) && (
                                    <div className="flex items-center">
                                        <div className={'scale-75 translate-x-2'}>
                                            <RatingStars type={"rating"} percent={props.garage.rating}/>
                                        </div>
                                        <div className={'font-xs sm:font-base'}>
                                        <span
                                            className="font-bold">{((props.garage.rating / 100) * 5).toFixed(2).replace('.', ',') ?? '0'}</span>&nbsp;({props.garage.review_count || '0'})
                                        </div>
                                    </div>
                                )}
                            </div>
                            <Hr/>
                        </div>
                        <div className="px-5 third lg:border-r-[0.5px] lg:border-neutral-100 lg:pr-8">
                            <div className="lg:flex lg:flex-col lg:justify-between lg:h-full">
                                {displayPrices && !!props.garage.calculation && (
                                    <GarageListItemPrice priceIsFixed={props.fixedPrice}
                                                         calculation={props.garage.calculation}
                                                         labour={props.calculation?.labour_steps}
                                                         parts={props.calculation?.parts}
                                                         service={props.service}/>
                                )}
                                {displayPremiumServices && (
                                    <GarageListItemPremiumServices garage={props.garage} service={props.service} organization={organization}/>
                                )}
                                {(GarageIsBookable(props.garage) && !props.showAsNonBookable) && (
                                    <button
                                        className="hidden lg:block bg-primary hover:bg-primary-100 text-white w-full py-3 px-5 rounded mt-3 font-base font-bold"
                                        onClick={() => onClick(props.garage)}>
                                        {t('garageList.gotoAppointmentselect')}
                                    </button>
                                )}
                                {(GarageIsNotBookableAndHasPhone(props.garage) || (GarageHasPhone(props.garage) && props.showAsNonBookable)) && (
                                    <div className={'hidden lg:block'}>
                                        {!showPhoneNumber && (
                                            <button
                                                className="bg-primary hover:bg-primary-100 text-white w-full py-3 px-5 rounded mt-3 font-base font-bold"
                                                onClick={togglePhoneNumber}>
                                                {t('garageList.showPhoneNumber')}
                                            </button>
                                        )}
                                        {showPhoneNumber && (
                                                <a className={'font-serif font-bold text-lg mb-1 w-full py-3 px-5 rounded mt-3 block text-center font-base font-bold'}
                                                   href={`tel:${props.garage.phone}`}
                                                   title={props.garage.name + ' anrufen'}>{props.garage.phone}</a>
                                        )}
                                    </div>
                                )}
                            </div>
                            {displayPrices && (
                                <Hr className="lg:hidden"/>
                            )}
                        </div>
                        <div className="px-5 fourth lg:pl-8 mt-2 lg:mt-0">
                            <div className="mb-5">
                                <div className="flex justify-between">
                                    <div className="flex text-base lg:flex-col">
                                        {(GarageIsBookable(props.garage) && !props.showAsNonBookable) && (
                                            <>
                                                <div className="mr-1 font-bold mb-2">
                                                    {OrganizationIsArval(organization) && (
                                                        props.garage.liveBookable
                                                        ? <CalendarCheckIcon className={'inline lg:hidden h-4 w-4 mr-1 -mt-1'}/>
                                                        : <PaperPlaneIcon className={'inline lg:hidden h-4 w-5 mr-1 -mt-1'}/>
                                                    )}
                                                    {t('garageList.nextAppointment')}
                                                </div>
                                                <div className="font-bold"
                                                     onClick={() => onClick(props.garage)}>
                                                    <div className={`
                                                lg:w-fit
                                                lg:px-3 lg:py-2
                                                lg:rounded
                                                lg:bg-primary
                                                text-primary lg:text-white
                                                underline lg:no-underline
                                                cursor-pointer mb-5`}>
                                                        {GarageHasAvailableDays(props.garage)
                                                            ? formatShortDate(props.garage.available_days[0].available_at)
                                                            : t('garageList.no-availability')
                                                        }
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        {(!GarageIsBookable(props.garage) || props.showAsNonBookable) && (
                                            <>
                                                <div className="mr-1 font-bold mb-2">Nicht online buchbar</div>
                                                {props.garage.street}, {props.garage.postcode} {props.garage.city}
                                            </>
                                        )}
                                        {
                                            (GarageIsBookableOnArval(organization, props.garage) && !props.showAsNonBookable)
                                            && <BookingType liveBookable={props.garage.liveBookable}/>
                                        }
                                    </div>
                                </div>
                                {(GarageIsBookable(props.garage) && !props.showAsNonBookable)  && (
                                    <button
                                        className="lg:hidden bg-primary hover:bg-primary-100 text-white w-full py-3 px-5 rounded mt-3 font-base font-bold"
                                        onClick={() => onClick(props.garage)}>
                                        {t('garageList.gotoAppointmentselect')}
                                    </button>
                                )}
                                {(!GarageIsBookable(props.garage) || props.showAsNonBookable) && (
                                    <>
                                        {!showPhoneNumber && (
                                            <button
                                                className="lg:hidden bg-primary hover:bg-primary-100 text-white w-full py-3 px-5 rounded mt-3 font-base font-bold"
                                                onClick={() => setShowPhoneNumber(true)}>
                                                {t('garageList.showPhoneNumber')}
                                            </button>
                                        )}
                                        {showPhoneNumber && (
                                            <div className={'lg:hidden'}>
                                                <a className={'font-serif font-bold text-lg mb-1 w-full py-3 px-5 rounded mt-3 font-base block text-center font-bold'}
                                                   href={`tel:${props.garage.phone}`}
                                                   title={props.garage.name + ' anrufen'}>{props.garage.phone}</a>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </StrictMode>
            </Transition>
        </div>
    );
})
